// =============================================================================
// Manifest wtyczek integracyjnych po stronie powłoki (krok 3)
//
// Ekran „Połącz" musi wiedzieć, o co zapytać właściciela firmy: Shopify chce
// domeny sklepu, Fakturownia subdomeny konta, GoPOS sluga organizacji. Prawdą
// o tym jest `describe()` konektora w `services/nexus-core`, ale powłoka nie ma
// jak jej przeczytać: katalog wychodzi z nexus-core wyłącznie punktem
// wymagającym super-admina, a to jest dokładnie ta bramka, którą krok 3 znosi.
//
// Dlatego manifest jest tu przepisany — świadome zdublowanie, nie przeoczenie.
// Alternatywy były dwie i obie gorsze: nowy punkt publiczny w nexus-core
// (powiększa powierzchnię API po to, żeby oddać trzy stałe) albo tabela
// w bazie (rozjedzie się z kodem konektora, bo nikt nie pamięta o dwóch
// miejscach). Rozjazd tego pliku z `connectorCatalog()` łapie test wartownik
// `tests/unit/apps/wtyczkiManifest.test.ts`, który czyta OBA źródła.
//
// Sekretu tu nie ma i być nie może — pole na token jest jedno dla wszystkich
// wtyczek, a jego wartość idzie prosto do sejfu (452).
// =============================================================================

export interface PoleWtyczki {
  /** Klucz w `genesis_connectors.config`. */
  key: string;
  label: string;
  placeholder?: string;
  /** Czy bez tego pola konektor nie ma jak się uwierzytelnić. */
  required?: boolean;
  help?: string;
}

/**
 * Jeden zakres integracji — jedna decyzja najemcy w ustawieniach połączenia
 * („co ta wtyczka ma u nas robić"). Wybór ląduje w
 * `genesis_connectors.config.zakresy` jako lista id; brak zapisu = wartości
 * `domyslny`. Lustrzane odbicie `ZakresWtyczki` z `describe()` konektora —
 * rozjazd łapie ten sam test wartownik, co przy polach konfiguracji.
 */
export interface ZakresWtyczkiPowloki {
  id: string;
  nazwa: string;
  opis: string;
  /** Moduły, którym zakres służy — pokazywane, żeby decyzja miała adres. */
  moduly: string[];
  domyslny?: boolean;
}

export interface ManifestWtyczki {
  /** Równe `modules.id` ORAZ `kind` konektora — patrz migracja 451. */
  kind: string;
  label: string;
  /** Czy wtyczka potrzebuje tokenu. */
  requiresSecret: boolean;
  /** Co wpisać w pole tokenu — mówione językiem właściciela sklepu. */
  secretLabel: string;
  secretHelp: string;
  configFields: PoleWtyczki[];
  /** Zakresy do włączenia osobno; brak pola = wtyczka działa w całości. */
  zakresy?: ZakresWtyczkiPowloki[];
  /**
   * Czy wtyczka umie oddać dane z WYBRANEGO miesiąca: dokumenty
   * (`dokumentyFkZaOkres` konektora FK) albo paragony (`zamowieniaSprzedazy`
   * wtyczki sprzedaży, okno po zamknięciu). Odbicie zdolności w nexus-core —
   * rozjazd łapie wartownik `wtyczkiManifest.test.ts`. Bez tej flagi przycisk
   * „Importuj historię" nie ma prawa się pokazać: kliknięcie skończyłoby się
   * `pominieto/brak_obslugi`.
   */
  importHistorii?: boolean;
  /**
   * Czy wtyczka umie zdiagnozować numery telefonów klientów programu
   * lojalnościowego (`szukajKontaktow` konektora — zdolność
   * `WtyczkaDiagnozyTelefonow` w nexus-core). Odbicie zdolności; rozjazd łapie
   * wartownik `wtyczkiManifest.test.ts` w obu kierunkach. Bez flagi sekcja
   * „Diagnoza numerów" się nie pokazuje — trasa odpowiedziałaby `brak_obslugi`.
   */
  diagnozaTelefonow?: boolean;
}

export const MANIFEST_WTYCZEK: readonly ManifestWtyczki[] = [
  {
    kind: 'shopify',
    label: 'Shopify',
    requiresSecret: true,
    secretLabel: 'Token Admin API',
    secretHelp:
      'Panel Shopify → Ustawienia → Aplikacje i kanały sprzedaży → Twórz aplikację. Token zaczyna się od „shpat_”.',
    configFields: [
      {
        key: 'shop_domain',
        label: 'Domena sklepu',
        placeholder: 'acme.myshopify.com',
        required: true,
        help: 'Pełny host xxx.myshopify.com.',
      },
    ],
  },
  {
    kind: 'fakturownia',
    label: 'Fakturownia',
    requiresSecret: true,
    secretLabel: 'api_token',
    secretHelp: 'Fakturownia → Ustawienia → Integracja → API. Skopiuj api_token.',
    configFields: [
      {
        key: 'account',
        label: 'Konto (subdomena)',
        placeholder: 'acme',
        required: true,
        help: 'Subdomena xxx.fakturownia.pl (samo „xxx”).',
      },
    ],
  },
  {
    kind: 'gopos',
    label: 'GoPOS',
    requiresSecret: true,
    secretLabel: 'Client secret',
    secretHelp:
      'GoPOS → Ustawienia → poświadczenia API. Sekretna połowa pary, którą generujesz u siebie; my nie rejestrujemy u dostawcy niczego.',
    configFields: [
      {
        key: 'organization_id',
        label: 'Identyfikator organizacji',
        placeholder: '11814',
        required: true,
        help: 'Liczba z GoPOS — ta sama, która stoi w adresach API lokalu.',
      },
      {
        key: 'client_id',
        label: 'Client ID',
        placeholder: '00000000-0000-0000-0000-000000000000',
        required: true,
        help: 'Publiczna połowa poświadczeń z panelu GoPOS. Sekretna połowa idzie do pola poniżej.',
      },
    ],
    // Lustro `describe().zakresy` GoPosConnector — pilnuje wartownik
    // `wtyczkiManifest.test.ts`. `klienci` startuje WYŁĄCZONE: dane osobowe.
    zakresy: [
      {
        id: 'wiedza',
        nazwa: 'Wiedza organizmu (Nexus)',
        opis: 'Karta lokalu i agregat sprzedaży z 30 dni w grafie wiedzy oraz skile Nexusa.',
        moduly: ['nexus'],
        domyslny: true,
      },
      {
        id: 'sprzedaz',
        nazwa: 'Sprzedaż w szczegółach (grafik, produkcja)',
        opis: 'Paragony z pozycjami, płatnościami i promocjami co kwadrans; zasilają prognozę obsady w grafiku i sygnał popytu dla produkcji.',
        moduly: ['lexshift', 'fabrica'],
        domyslny: true,
      },
      {
        id: 'pracownicy',
        nazwa: 'Kasjerzy → pracownicy (LexShift)',
        opis: 'Kasjer z paragonu dopina się do kartoteki pracownika po identyfikatorze z kasy; sprzedaż na pracownika w grafiku.',
        moduly: ['lexshift'],
        domyslny: true,
      },
      {
        id: 'klienci',
        nazwa: 'Klienci z paragonów (dane osobowe)',
        opis: 'Imię, telefon i grupa klienta z GoCRM do wspólnej kartoteki klientów. Bez tego zakresu system liczy stałych klientów po pseudonimie, bez danych osobowych.',
        moduly: ['nexus'],
      },
    ],
    // `zamowieniaSprzedazy` po `closed_at` miesiąca — import historii sprzedaży.
    importHistorii: true,
  },
  {
    kind: 'saldeo',
    label: 'SaldeoSMART',
    requiresSecret: true,
    secretLabel: 'api_token',
    secretHelp:
      'SaldeoSMART → Ustawienia konta → API. Skopiuj api_token — służy wyłącznie do podpisywania żądań, nigdy nie jest wysyłany wprost.',
    configFields: [
      {
        key: 'uzytkownik',
        label: 'Użytkownik API',
        placeholder: 'api-firma',
        required: true,
        help: 'Login, którym logujesz się do SaldeoSMART (konto z aktywnym API).',
      },
      {
        key: 'firma',
        label: 'Identyfikator firmy (COMPANY_PROGRAM_ID)',
        placeholder: 'acme-01',
        required: true,
        help: 'Identyfikator nadany firmie przy company/create lub company/synchronize — unikalny w całym SaldeoSMART.',
      },
      {
        key: 'srodowisko',
        label: 'Środowisko',
        placeholder: 'produkcja',
        required: false,
        help: 'Wpisz „test”, aby łączyć się z serwerem testowym saldeo-test.brainshare.pl. Puste pole = produkcja.',
      },
    ],
    zakresy: [
      {
        id: 'wiedza',
        nazwa: 'Wiedza organizmu (Nexus)',
        opis: 'Agregaty obiegu dokumentów w grafie wiedzy i dwa skile Nexusa.',
        moduly: ['nexus'],
        domyslny: true,
      },
      {
        id: 'dokumenty',
        nazwa: 'Obieg dokumentów (Provisio)',
        opis: 'Import faktur zakupowych i dokumentów z SaldeoSMART do rejestru faktur Provisio.',
        moduly: ['provisio'],
      },
      {
        id: 'dostawcy',
        nazwa: 'Baza dostawców (Provisio)',
        opis: 'Synchronizacja kontrahentów z SaldeoSMART jako dostawców w Provisio.',
        moduly: ['provisio'],
      },
      {
        id: 'kadry',
        nazwa: 'Kartoteka pracowników (Saldeo Kadry)',
        opis: 'Pracownicy z Universe trafiają do modułu Kadr SaldeoSMART i są tam aktualizowani: imię i nazwisko, data urodzenia, kontakt, umowa i stanowisko, badania i BHP, dezaktywacja przy odejściu. Bez PESEL, konta bankowego i adresu — te pola zostają własnością biura w Saldeo.',
        moduly: ['lexshift'],
      },
      {
        id: 'ewidencja',
        nazwa: 'Ewidencja czasu pracy i grafik (dokumenty kadrowe)',
        opis: 'Raz w miesiącu do modułu Kadr SaldeoSMART jadą: ewidencja czasu pracy każdego pracownika (PDF), grafik miesiąca (PDF, po publikacji) i zbiorcze zestawienie godzin (CSV dla programu płacowego). Wymaga włączonej „Kartoteki pracowników" — dokument dopina się do pracownika po jego numerze w Saldeo.',
        moduly: ['lexshift'],
      },
    ],
    importHistorii: true,
  },
  {
    kind: 'gocrm',
    label: 'GoCRM',
    requiresSecret: true,
    secretLabel: 'Client secret',
    secretHelp:
      'GoCRM → Ustawienia → poświadczenia API. To INNA para niż w kasie GoPOS — program lojalnościowy ma własną organizację i własne poświadczenia.',
    configFields: [
      {
        key: 'organization_id',
        label: 'Identyfikator organizacji GoCRM',
        placeholder: '123',
        required: true,
        help: 'Liczba z GoCRM — inna niż identyfikator lokalu w kasie GoPOS.',
      },
      {
        key: 'client_id',
        label: 'Client ID',
        placeholder: '00000000-0000-0000-0000-000000000000',
        required: true,
        help: 'Publiczna połowa poświadczeń z ustawień GoCRM. Sekretna połowa idzie do pola poniżej.',
      },
    ],
    diagnozaTelefonow: true,
  },
];

/**
 * Zakresy zaznaczone na wejściu do formularza: zapis najemcy, jeśli jest
 * (tylko id znane manifestowi), inaczej wartości `domyslny`. Ta sama reguła,
 * którą po stronie zaciągu stosuje `zakresyWtyczki.ts` w nexus-core.
 */
export function zakresyPoczatkowe(
  manifest: ManifestWtyczki,
  config: Record<string, unknown> | null | undefined
): string[] {
  const zadeklarowane = manifest.zakresy ?? [];
  if (zadeklarowane.length === 0) return [];
  const zapisane = config?.['zakresy'];
  if (Array.isArray(zapisane)) {
    const znane = new Set(zadeklarowane.map((z) => z.id));
    return zapisane.filter((z): z is string => typeof z === 'string' && znane.has(z));
  }
  return zadeklarowane.filter((z) => z.domyslny).map((z) => z.id);
}

const WEDLUG_RODZAJU = new Map(MANIFEST_WTYCZEK.map((w) => [w.kind, w]));

/** Manifest wtyczki albo `undefined`, gdy powłoka jej nie zna. */
export function manifestWtyczki(kind: string): ManifestWtyczki | undefined {
  return WEDLUG_RODZAJU.get(kind);
}

/**
 * Czego brakuje, żeby połączenie miało sens. Zwraca etykiety pustych pól
 * wymaganych — pusta tablica znaczy „można wysyłać".
 *
 * `maJuzSekret` pozwala zapisać samą poprawkę konfiguracji bez ponownego
 * wklejania tokenu: pole tokenu jest puste PRZY KAŻDYM otwarciu (backend go nie
 * oddaje), więc traktowanie pustego jako braku zmuszałoby do rotacji przy
 * każdej literówce w adresie sklepu.
 */
export function brakiPolaczenia(
  manifest: ManifestWtyczki,
  config: Record<string, string>,
  sekret: string,
  maJuzSekret: boolean
): string[] {
  const braki = manifest.configFields
    .filter((f) => f.required && !(config[f.key] ?? '').trim())
    .map((f) => f.label);

  if (manifest.requiresSecret && !sekret.trim() && !maJuzSekret) {
    braki.push(manifest.secretLabel);
  }
  return braki;
}

/**
 * Konfiguracja do wysłania: tylko zadeklarowane klucze, tylko niepuste,
 * przycięte. Pusty łańcuch nie jest konfiguracją, tylko nieodpowiedzią, a
 * zapisany zmusiłby konektor do rozstrzygania, czy „” znaczy brak, czy wartość.
 */
export function konfiguracjaDoWyslania(
  manifest: ManifestWtyczki,
  config: Record<string, string>
): Record<string, string> {
  const wynik: Record<string, string> = {};
  for (const f of manifest.configFields) {
    const v = (config[f.key] ?? '').trim();
    if (v) wynik[f.key] = v;
  }
  return wynik;
}

/**
 * Kształt identyfikatora firmy w systemie FK (Saldeo: COMPANY_PROGRAM_ID).
 * Odbicie `normalizujFirme` z konektora — wartość spoza tego wzorca nie dojdzie
 * nawet do API źródła. Dwukropek jest dozwolony, bo integracje programów FK
 * nadają identyfikatory postaci `PROGRAM::FIRMA` (`SYMFONIA_ERP::OGYY`).
 */
const WZORZEC_IDENTYFIKATORA_FIRMY = /^[A-Za-z0-9_.:-]{1,64}$/;

/**
 * Ostrzeżenie pod polem „Firma", gdy wpisana wartość nie jest identyfikatorem.
 * Z produkcji 19.08.2026: najemca wkleił tu NAZWĘ firmy z listy wykrywania
 * („ING BANK ŚLĄSKI") i wtyczka przestała wysyłać żądania — bez tego zdania
 * jedyną informacją zwrotną byłby brak jakiejkolwiek informacji zwrotnej.
 * `null` znaczy „nie mam zastrzeżeń" (pole puste ocenia `brakiPolaczenia`).
 */
export function ostrzezenieIdentyfikatoraFirmy(wartosc: string): string | null {
  const czysta = wartosc.trim();
  if (!czysta) return null;
  if (WZORZEC_IDENTYFIKATORA_FIRMY.test(czysta)) return null;
  return 'To nie wygląda na identyfikator (COMPANY_PROGRAM_ID) — dozwolone są litery bez ogonków, cyfry, kropka, dwukropek, podkreślenie i myślnik, bez spacji. Nazwa firmy tu nie zadziała; użyj „Wykryj identyfikator firmy".';
}

/**
 * Czy wtyczka ma synchronizację FK → Provisio (przycisk „Synchronizuj teraz").
 * Prawdą o zdolności jest interfejs `WtyczkaFk` konektora w nexus-core, ale
 * powłoka widzi jej wierne odbicie w zadeklarowanych zakresach Provisio —
 * wartownik `ProvisioFkSync.test.ts` pilnuje, że jedno nie istnieje bez
 * drugiego.
 */
export function maSynchronizacjeFk(manifest: ManifestWtyczki): boolean {
  return (manifest.zakresy ?? []).some((z) => z.id === 'dokumenty' || z.id === 'dostawcy');
}

/**
 * Czy wtyczka zasila SPRZEDAŻ (zakres `sprzedaz` → `SprzedazSync`, tabele
 * `sales_*`). Druga zdolność obok FK; ta sama wtyczka nie deklaruje obu.
 */
export function maSynchronizacjeSprzedazy(manifest: ManifestWtyczki): boolean {
  return (manifest.zakresy ?? []).some((z) => z.id === 'sprzedaz');
}

/**
 * Czy pokazać sekcję „Kadry": wtyczka deklaruje zakres `kadry` I najemca go
 * WŁĄCZYŁ. Drugi człon jest tu istotą, a nie ostrożnością — zakres kadrowy
 * jest jedynym, przez który dane osobowe WYCHODZĄ z Universe do cudzego
 * systemu, więc jest domyślnie wyłączony i sekcja bez jego włączenia
 * pokazywałaby stan czegoś, co się nie dzieje.
 *
 * `rodzajSynchronizacji()` celowo tego nie obsługuje: kadry nie są trzecim
 * wariantem przycisku „Synchronizuj teraz", tylko własną sekcją z własnym
 * przyciskiem i własną listą do uzgodnienia.
 */
export function maSynchronizacjeKadr(
  manifest: ManifestWtyczki,
  config: Record<string, unknown> | null | undefined
): boolean {
  if (!(manifest.zakresy ?? []).some((z) => z.id === 'kadry')) return false;
  return zakresyPoczatkowe(manifest, config).includes('kadry');
}

/** Trasa nexus-core dla „Synchronizuj kartotekę teraz". */
export const TRASA_SYNCHRONIZACJI_KADR = '/api/nexus/genesis/connectors/kadry-sync';

/** Trasa nexus-core dla wskazania kandydata przy `AMBIGUOUS`. */
export const TRASA_UZGODNIENIA_KADR = '/api/nexus/genesis/connectors/kadry-uzgodnij';

/**
 * Odpowiedź `kadry-sync` → jedno zdanie dla najemcy. Mapa jawna, jak przy
 * dwóch pozostałych synchronizatorach: każdy `wynik`/`powod` ma tu swoje
 * zdanie, a nieznany wariant dostaje uczciwe „nie wiem" zamiast udawanego
 * sukcesu.
 */
export function opiszWynikSynchronizacjiKadr(cialo: unknown): string {
  const c = (cialo ?? {}) as Record<string, unknown>;
  const wynik = typeof c.wynik === 'string' ? c.wynik : '';
  if (wynik === 'ok') {
    const l = (c.liczniki ?? {}) as Record<string, unknown>;
    const n = (k: string) => (typeof l[k] === 'number' ? (l[k] as number) : 0);
    if (n('wyslani') === 0 && n('do_uzgodnienia') === 0 && n('konflikty') === 0) {
      return 'Kartoteka jest zgodna z Saldeo — nie było czego wysyłać.';
    }
    const czesci = [
      `${n('wyslani')} wysłanych`,
      ...(n('utworzeni') ? [`${n('utworzeni')} założonych`] : []),
      ...(n('zaktualizowani') ? [`${n('zaktualizowani')} zaktualizowanych`] : []),
    ];
    const uwagi: string[] = [];
    if (n('do_uzgodnienia') > 0) uwagi.push(`${n('do_uzgodnienia')} do uzgodnienia`);
    if (n('konflikty') > 0) uwagi.push(`${n('konflikty')} z zajętym akronimem`);
    if (n('niepoprawni') > 0) uwagi.push(`${n('niepoprawni')} odrzuconych przez Saldeo`);
    if (n('pominieci_niekompletni') > 0) {
      uwagi.push(`${n('pominieci_niekompletni')} bez imienia lub nazwiska`);
    }
    const ogon = uwagi.length ? ` Do przejrzenia: ${uwagi.join(', ')}.` : '';
    return `Kartoteka: ${czesci.join(', ')}.${ogon}`;
  }
  if (wynik === 'za_czesto') {
    const s = typeof c.odczekaj_sekundy === 'number' ? c.odczekaj_sekundy : 60;
    return `Przed chwilą synchronizowano — odczekaj ${s} s.`;
  }
  if (wynik === 'brak_polaczenia') return 'Połączenie nie istnieje albo jest wyłączone.';
  if (wynik === 'blad') {
    const k = typeof c.komunikat === 'string' ? c.komunikat : '';
    // Najczęstsza odmowa modułu Kadr ma własne zdanie, bo naprawa nie jest po
    // naszej stronie ani po stronie najemcy, tylko po stronie biura.
    if (k.includes('6001')) {
      return 'Użytkownik API nie ma uprawnienia „Synchronizacja Modułu kadr" — biuro włącza je w SaldeoSMART: Konfiguracja → Użytkownicy → edycja użytkownika → uprawnienia API.';
    }
    return k ? `Saldeo odmówiło: ${k}` : 'Wysyłka kartoteki nie powiodła się.';
  }
  if (wynik === 'pominieto') {
    const powod = typeof c.powod === 'string' ? c.powod : '';
    if (powod === 'zakres_wylaczony') {
      return 'Zakres „Kartoteka pracowników" jest wyłączony — zaznacz go i zapisz połączenie.';
    }
    if (powod === 'brak_sekretu') return 'Brak tokenu w sejfie — uzupełnij połączenie.';
    if (powod === 'brak_manifestu' || powod === 'nieznany_konektor') {
      return 'Wtyczka nie jest znana synchronizatorowi kadr.';
    }
    return `Pominięto (${powod || 'bez powodu'}).`;
  }
  return 'Nieznany wynik synchronizacji kartoteki.';
}

/**
 * Zieleń tylko wtedy, gdy nie ma czego przeglądać. Przebieg, po którym dwóch
 * pracowników czeka na uzgodnienie, jest „udany" w sensie technicznym i
 * NIEUDANY w sensie, który interesuje administratora.
 */
export function sukcesSynchronizacjiKadr(cialo: unknown): boolean {
  const c = (cialo ?? {}) as Record<string, unknown>;
  if (c.wynik !== 'ok') return false;
  const l = (c.liczniki ?? {}) as Record<string, unknown>;
  const n = (k: string) => (typeof l[k] === 'number' ? (l[k] as number) : 0);
  return (
    n('do_uzgodnienia') === 0 &&
    n('konflikty') === 0 &&
    n('niepoprawni') === 0 &&
    n('pominieci_niekompletni') === 0
  );
}

/** Odpowiedź `kadry-uzgodnij` → jedno zdanie. */
export function opiszWynikUzgodnieniaKadr(cialo: unknown): string {
  const c = (cialo ?? {}) as Record<string, unknown>;
  const wynik = typeof c.wynik === 'string' ? c.wynik : '';
  if (wynik === 'ok') {
    return 'Tożsamość wskazana — dane pracownika pojadą do Saldeo przy najbliższej synchronizacji.';
  }
  if (wynik === 'brak_wpisu') return 'Ten pracownik nie czeka już na uzgodnienie.';
  if (wynik === 'nie_kandydat') {
    return 'Wskazany pracownik nie jest jednym z kandydatów z Saldeo — odśwież listę i wybierz ponownie.';
  }
  if (wynik === 'brak_polaczenia') return 'Połączenie nie istnieje albo jest wyłączone.';
  return 'Nieznany wynik uzgodnienia.';
}

/**
 * Status z mapy tożsamości → zdanie po polsku dla listy „do uzgodnienia".
 * Mapa jawna: kod, którego nie znamy, nie udaje, że coś znaczy.
 */
export function opiszStanTozsamosci(status: string | null | undefined): string {
  switch (status) {
    case 'CREATED':
      return 'Założony w Saldeo';
    case 'MERGED':
      return 'Zaktualizowany w Saldeo';
    case 'CONFLICT':
      return 'Akronim zajęty w Saldeo — zmień kod pracownika w kartotece';
    case 'NOT_VALID':
      return 'Saldeo odrzuciło dane — popraw pole wskazane w komunikacie';
    case 'AMBIGUOUS':
      return 'W Saldeo pasuje więcej niż jeden pracownik — wskaż właściwego';
    case 'ERROR':
      return 'Wysyłka nie doszła do skutku — spróbujemy ponownie za godzinę';
    default:
      return 'Jeszcze nie wysłany';
  }
}

/** Rodzaj synchronizatora za przyciskiem „Synchronizuj teraz" — decyduje o trasie i o zdaniu wyniku. */
export type RodzajSynchronizacji = 'fk' | 'sprzedaz';

export function rodzajSynchronizacji(manifest: ManifestWtyczki): RodzajSynchronizacji | null {
  if (maSynchronizacjeFk(manifest)) return 'fk';
  if (maSynchronizacjeSprzedazy(manifest)) return 'sprzedaz';
  return null;
}

/** Trasa nexus-core dla „Synchronizuj teraz" danego rodzaju. */
export function trasaSynchronizacji(rodzaj: RodzajSynchronizacji): string {
  return rodzaj === 'fk'
    ? '/api/nexus/genesis/connectors/fk-sync'
    : '/api/nexus/genesis/connectors/sprzedaz-sync';
}

/** Trasa nexus-core dla „Importuj historię" danego rodzaju. */
export function trasaImportuHistorii(rodzaj: RodzajSynchronizacji): string {
  return rodzaj === 'fk'
    ? '/api/nexus/genesis/connectors/fk-historia'
    : '/api/nexus/genesis/connectors/sprzedaz-historia';
}

/**
 * Odpowiedź `sprzedaz-sync` / `sprzedaz-historia` → jedno zdanie dla najemcy.
 * Mapa jawna: każdy `wynik`/`powod` synchronizatora ma tu swoje zdanie.
 */
export function opiszWynikSynchronizacjiSprzedazy(cialo: unknown): string {
  const c = (cialo ?? {}) as Record<string, unknown>;
  const wynik = typeof c.wynik === 'string' ? c.wynik : '';
  if (wynik === 'ok') {
    const l = (c.liczniki ?? {}) as Record<string, unknown>;
    const n = (k: string) => (typeof l[k] === 'number' ? (l[k] as number) : 0);
    const bledy = Array.isArray(c.bledy)
      ? c.bledy.filter((b): b is string => typeof b === 'string')
      : [];
    const zdanie =
      `Paragony: ${n('zamowienia')} (nowe ${n('nowe')}, zmienione ${n('zmienione')}), ` +
      `pozycji ${n('pozycje')}, dni ${n('dni')}, produktów z karty ${n('produkty')}.`;
    const braki: string[] = [];
    if (n('bez_produktu') > 0) braki.push(`${n('bez_produktu')} pozycji bez produktu w kartotece`);
    if (n('bez_kasjera') > 0) braki.push(`${n('bez_kasjera')} paragonów bez dopasowanego kasjera`);
    const ogon = braki.length ? ` Do przejrzenia: ${braki.join(', ')}.` : '';
    if (bledy.length > 0) {
      return `${zdanie}${ogon} Część paczek nie weszła (${bledy.length}) — sprawdź dziennik.`;
    }
    return zdanie + ogon;
  }
  if (wynik === 'pominieto') {
    const powod = typeof c.powod === 'string' ? c.powod : '';
    if (powod === 'zakres_wylaczony') {
      return 'Zakres „Sprzedaż w szczegółach" jest wyłączony — włącz go w ustawieniach połączenia.';
    }
    if (powod === 'brak_sekretu')
      return 'Brak sekretu w sejfie — połącz wtyczkę ponownie z Client Secret.';
    if (powod === 'brak_obslugi') return 'Ta wtyczka nie umie oddać paragonów z wybranego okresu.';
    if (powod === 'brak_manifestu' || powod === 'nieznany_konektor') {
      return 'Wtyczka nie jest znana synchronizatorowi sprzedaży.';
    }
    return `Pominięto (${powod || 'bez powodu'}).`;
  }
  if (wynik === 'brak_polaczenia') return 'Brak aktywnego połączenia dla tej wtyczki.';
  if (wynik === 'za_czesto') {
    const s = typeof c.odczekaj_sekundy === 'number' ? c.odczekaj_sekundy : 60;
    return `Za często — odczekaj ${s} s.`;
  }
  if (wynik === 'zly_okres') return 'Wybierz poprawny miesiąc.';
  if (wynik === 'blad') {
    const k = typeof c.komunikat === 'string' ? c.komunikat : '';
    return k ? `Nie udało się: ${k}` : 'Nie udało się pobrać paragonów ze źródła.';
  }
  return 'Nieznany wynik synchronizacji.';
}

/** Zieleń tylko dla pełnego sukcesu; `ok` z błędami paczek — bursztyn. */
export function sukcesSynchronizacjiSprzedazy(cialo: unknown): boolean {
  const c = (cialo ?? {}) as Record<string, unknown>;
  if (c.wynik !== 'ok') return false;
  return !(Array.isArray(c.bledy) && c.bledy.length > 0);
}

/**
 * Odpowiedź `POST /api/nexus/genesis/connectors/fk-sync` → jedno zdanie dla
 * najemcy. Mapa jest jawna po obu stronach: każdy `wynik`/`powod` synchronizatora
 * ma tu swoje zdanie, a nieznany wariant dostaje uczciwe „nieznany wynik"
 * zamiast udawanego sukcesu.
 */
export function opiszWynikSynchronizacjiFk(cialo: unknown): string {
  const c = (cialo ?? {}) as Record<string, unknown>;
  const wynik = typeof c.wynik === 'string' ? c.wynik : '';
  if (wynik === 'ok') {
    const l = (c.liczniki ?? {}) as Record<string, unknown>;
    const n = (k: string) => (typeof l[k] === 'number' ? (l[k] as number) : 0);
    // Odpowiedzi źródła, które przerwały pobranie (np. zły identyfikator firmy)
    // — bez nich „0 dostawców" wyglądałoby na sukces po odmowie API.
    const bledy = Array.isArray(c.bledy)
      ? c.bledy.filter((b): b is string => typeof b === 'string')
      : [];
    if (bledy.length > 0) {
      const zaimportowano = n('dostawcy') + n('faktury_nowe') + n('faktury_oplacone') > 0;
      const czescOk = zaimportowano
        ? `Częściowo zsynchronizowano (${n('dostawcy')} dostawców, ${n('faktury_nowe')} nowych faktur). `
        : '';
      return `${czescOk}Źródło odmówiło: ${bledy.join('; ')}. Sprawdź konfigurację połączenia.`;
    }
    const czesci = [
      `${n('dostawcy')} dostawców`,
      `${n('faktury_nowe')} nowych faktur`,
      ...(n('faktury_oplacone') ? [`${n('faktury_oplacone')} oznaczono jako opłacone`] : []),
      ...(n('pominiete_dokumenty') ? [`${n('pominiete_dokumenty')} dokumentów pominięto`] : []),
    ];
    return `Zsynchronizowano: ${czesci.join(', ')}.`;
  }
  if (wynik === 'za_czesto') {
    const s = typeof c.odczekaj_sekundy === 'number' ? c.odczekaj_sekundy : 60;
    return `Przed chwilą synchronizowano — odczekaj ${s} s.`;
  }
  if (wynik === 'brak_polaczenia') return 'Połączenie nie istnieje albo jest wyłączone.';
  if (wynik === 'pominieto') {
    const powod = typeof c.powod === 'string' ? c.powod : '';
    if (powod === 'zakresy_wylaczone') {
      return 'Żaden zakres Provisio nie jest włączony — zaznacz „Obieg dokumentów" lub „Baza dostawców" i zapisz połączenie.';
    }
    if (powod === 'brak_sekretu') return 'Brak tokenu w sejfie — uzupełnij połączenie.';
    return 'Ta wtyczka nie obsługuje synchronizacji Provisio.';
  }
  return 'Nieznany wynik synchronizacji.';
}

/**
 * Czy odpowiedź synchronizacji to sukces DO POKAZANIA na zielono. `wynik: 'ok'`
 * z niepustym `bledy` znaczy „API źródła odmówiło" — flaga `success` z serwera
 * mówi tylko, że przebieg się odbył, więc kolor rozstrzygamy tutaj.
 */
export function sukcesSynchronizacjiFk(cialo: unknown): boolean {
  const c = (cialo ?? {}) as Record<string, unknown>;
  return c.wynik === 'ok' && !(Array.isArray(c.bledy) && c.bledy.length > 0);
}

/**
 * Czy pokazać „Importuj historię": wtyczka umie listować po miesiącu I najemca
 * włączył zakres, który ta historia zasila — `dokumenty` (FK → Provisio) albo
 * `sprzedaz` (paragony → sales_*). Historia bez zakresu byłaby obwodnicą jego
 * decyzji (synchronizator i tak odmówi: `zakres_wylaczony`).
 */
export function maImportHistorii(
  manifest: ManifestWtyczki,
  config: Record<string, unknown> | null | undefined
): boolean {
  if (!manifest.importHistorii) return false;
  const wlaczone = zakresyPoczatkowe(manifest, config);
  const rodzaj = rodzajSynchronizacji(manifest);
  if (rodzaj === 'fk') return wlaczone.includes('dokumenty');
  if (rodzaj === 'sprzedaz') return wlaczone.includes('sprzedaz');
  return false;
}

/**
 * Wartość `<input type="month">` (`RRRR-MM`) → okres do `fk-historia`.
 * `null` dla wszystkiego, co nie jest poprawnym miesiącem — przycisk jest
 * wtedy nieaktywny, zamiast wysyłać NaN.
 */
export function okresZPola(wartosc: string): { rok: number; miesiac: number } | null {
  const m = /^(\d{4})-(\d{2})$/.exec(wartosc.trim());
  if (!m) return null;
  const rok = Number(m[1]);
  const miesiac = Number(m[2]);
  if (rok < 2000 || rok > 2100 || miesiac < 1 || miesiac > 12) return null;
  return { rok, miesiac };
}

/** Domyślny miesiąc pola: poprzedni względem `teraz` — bieżący łapie już sync 10-dniowy. */
export function domyslnyOkres(teraz: Date = new Date()): string {
  const d = new Date(teraz.getFullYear(), teraz.getMonth() - 1, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * Odpowiedź `POST /api/nexus/genesis/connectors/fk-historia` → jedno zdanie.
 * Ta sama zasada, co przy synchronizacji: każdy wariant ma swoje zdanie,
 * nieznany dostaje uczciwe „nie wiem". Zero znalezionych to informacja,
 * nie błąd — źródło może po prostu nie mieć dokumentów z tego miesiąca
 * (albo użytkownik API nie ma do nich uprawnień; API tego nie rozróżnia).
 */
export function opiszWynikImportuHistorii(cialo: unknown): string {
  const c = (cialo ?? {}) as Record<string, unknown>;
  const wynik = typeof c.wynik === 'string' ? c.wynik : '';
  if (wynik === 'ok') {
    const l = (c.liczniki ?? {}) as Record<string, unknown>;
    const n = (k: string) => (typeof l[k] === 'number' ? (l[k] as number) : 0);
    const o = (c.okres ?? {}) as Record<string, unknown>;
    const okres =
      typeof o.rok === 'number' && typeof o.miesiac === 'number'
        ? `${String(o.miesiac).padStart(2, '0')}/${o.rok}`
        : 'wybrany miesiąc';
    if (n('znalezione') === 0) {
      return `Za ${okres} źródło nie oddało żadnych faktur kosztowych. Jeśli dokumenty są w systemie, sprawdź uprawnienia użytkownika API do tej firmy.`;
    }
    const czesci = [
      `${n('znalezione')} dokumentów w źródle`,
      `${n('faktury_nowe')} nowych faktur`,
      ...(n('faktury_oplacone') ? [`${n('faktury_oplacone')} oznaczono jako opłacone`] : []),
      ...(n('pominiete_dokumenty')
        ? [`${n('pominiete_dokumenty')} pominięto (niekompletne albo już były)`]
        : []),
    ];
    return `Import za ${okres}: ${czesci.join(', ')}.`;
  }
  if (wynik === 'za_czesto') {
    const s = typeof c.odczekaj_sekundy === 'number' ? c.odczekaj_sekundy : 60;
    return `Przed chwilą importowano — odczekaj ${s} s.`;
  }
  if (wynik === 'brak_polaczenia') return 'Połączenie nie istnieje albo jest wyłączone.';
  if (wynik === 'zly_okres') return 'Wybierz poprawny miesiąc (rok 2000–2100).';
  if (wynik === 'blad') {
    const k = typeof c.komunikat === 'string' ? c.komunikat : '';
    return k ? `Źródło odmówiło: ${k}` : 'Źródło odmówiło importu.';
  }
  if (wynik === 'pominieto') {
    const powod = typeof c.powod === 'string' ? c.powod : '';
    if (powod === 'zakres_wylaczony') {
      return 'Zakres „Obieg dokumentów" jest wyłączony — zaznacz go i zapisz połączenie.';
    }
    if (powod === 'brak_sekretu') return 'Brak tokenu w sejfie — uzupełnij połączenie.';
    if (powod === 'brak_obslugi') return 'Ta wtyczka nie umie pobierać dokumentów po miesiącu.';
    return 'Ta wtyczka nie obsługuje importu historii.';
  }
  return 'Nieznany wynik importu.';
}

/** Zieleń tylko dla `ok` z co najmniej jedną nową fakturą; zero znalezionych to bursztyn. */
export function sukcesImportuHistorii(cialo: unknown): boolean {
  const c = (cialo ?? {}) as Record<string, unknown>;
  if (c.wynik !== 'ok') return false;
  const l = (c.liczniki ?? {}) as Record<string, unknown>;
  return typeof l.znalezione === 'number' && l.znalezione > 0;
}

/** Jedna firma z konta źródła FK — identyfikator do pola „Firma" + nazwa. */
export interface FirmaZrodlaFk {
  id: string | null;
  nazwa: string | null;
}

/**
 * Odpowiedź `POST /api/nexus/genesis/connectors/fk-firmy` → lista firm do
 * wyświetlenia albo jedno zdanie, czemu listy nie ma. Ta sama zasada, co przy
 * synchronizacji: każdy wariant serwera ma tu swoje zdanie, nieznany dostaje
 * uczciwe „nieznany wynik".
 */
export function opiszWynikFirmFk(cialo: unknown): { firmy: FirmaZrodlaFk[] } | { blad: string } {
  const c = (cialo ?? {}) as Record<string, unknown>;
  const wynik = typeof c.wynik === 'string' ? c.wynik : '';
  if (wynik === 'ok') {
    const surowe = Array.isArray(c.firmy) ? c.firmy : [];
    const firmy = surowe.map((f) => {
      const w = (f ?? {}) as Record<string, unknown>;
      return {
        id: typeof w.id === 'string' && w.id.trim() ? w.id : null,
        nazwa: typeof w.nazwa === 'string' && w.nazwa.trim() ? w.nazwa : null,
      };
    });
    if (firmy.length === 0) {
      return { blad: 'Konto nie ma żadnych firm w źródle — załóż firmę w panelu dostawcy.' };
    }
    return { firmy };
  }
  if (wynik === 'za_czesto') {
    const s = typeof c.odczekaj_sekundy === 'number' ? c.odczekaj_sekundy : 10;
    return { blad: `Przed chwilą pobierano listę — odczekaj ${s} s.` };
  }
  if (wynik === 'brak_polaczenia') return { blad: 'Połączenie nie istnieje albo jest wyłączone.' };
  if (wynik === 'blad') {
    const komunikat = typeof c.komunikat === 'string' ? c.komunikat : 'nieznany błąd źródła';
    return { blad: `Źródło odpowiedziało błędem: ${komunikat}` };
  }
  if (wynik === 'pominieto') {
    const powod = typeof c.powod === 'string' ? c.powod : '';
    if (powod === 'brak_sekretu') return { blad: 'Brak tokenu w sejfie — uzupełnij połączenie.' };
    return { blad: 'Ta wtyczka nie udostępnia listy firm.' };
  }
  return { blad: 'Nieznany wynik pobrania listy firm.' };
}

/**
 * Czy pokazać sekcję „Ewidencja czasu pracy": wtyczka deklaruje zakres
 * `ewidencja` I najemca go włączył I włączona jest KARTOTEKA.
 *
 * Trzeci człon nie jest ostrożnością: dokument kadrowy dopina się po
 * `EMPLOYEE_ID`, który powstaje wyłącznie w zakresie `kadry`. Sekcja bez
 * kartoteki pokazywałaby przycisk, który za każdym razem odmawia — a przycisk,
 * który zawsze odmawia, uczy ludzi, że komunikaty tej karty nic nie znaczą.
 */
export function maWysylkeEwidencji(
  manifest: ManifestWtyczki,
  config: Record<string, unknown> | null | undefined
): boolean {
  if (!(manifest.zakresy ?? []).some((z) => z.id === 'ewidencja')) return false;
  const wlaczone = zakresyPoczatkowe(manifest, config);
  return wlaczone.includes('ewidencja') && maSynchronizacjeKadr(manifest, config);
}

/** Trasa nexus-core dla „Wyślij ewidencję za miesiąc" (także korekta). */
export const TRASA_EWIDENCJI_KADR = '/api/nexus/genesis/connectors/kadry-ewidencja';

/**
 * Odpowiedź `kadry-ewidencja` → jedno zdanie dla najemcy. Mapa jawna, jak przy
 * kartotece: każdy `wynik`/`powod` ma tu swoje zdanie, bo „pominięto" bez
 * powodu jest dla administratora nieodróżnialne od awarii.
 */
export function opiszWynikEwidencji(cialo: unknown): string {
  const c = (cialo ?? {}) as Record<string, unknown>;
  const wynik = typeof c.wynik === 'string' ? c.wynik : '';
  if (wynik === 'ok') {
    const l = (c.liczniki ?? {}) as Record<string, unknown>;
    const n = (k: string) => (typeof l[k] === 'number' ? (l[k] as number) : 0);
    const czesci: string[] = [];
    if (n('ewidencje') > 0) czesci.push(`${n('ewidencje')} ewidencji`);
    if (n('grafik') > 0) czesci.push('grafik miesiąca');
    if (n('zestawienie') > 0) czesci.push('zestawienie CSV');
    const uwagi: string[] = [];
    if (n('bez_tozsamosci') > 0) {
      uwagi.push(`${n('bez_tozsamosci')} pracowników czeka na uzgodnienie w Saldeo`);
    }
    if (n('bez_zmian') > 0 && czesci.length === 0) {
      uwagi.push('nic się nie zmieniło od ostatniej wysyłki');
    }
    if (n('konflikty') > 0) uwagi.push(`${n('konflikty')} odrzuconych jako duplikat`);
    if (n('niepoprawne') > 0) uwagi.push(`${n('niepoprawne')} odrzuconych przez Saldeo`);
    const ogon = uwagi.length ? ` Do przejrzenia: ${uwagi.join(', ')}.` : '';
    if (czesci.length === 0) return `Nie było czego wysyłać.${ogon}`;
    // Zestawienie w wyniku znaczy, że miesiąc się ZAMKNĄŁ — administrator ma
    // to zobaczyć wprost, bo od tej chwili automat już do niego nie wróci.
    const zamkniete = n('zestawienie') > 0 ? ' Miesiąc zamknięty.' : '';
    return `Wysłano: ${czesci.join(', ')}.${zamkniete}${ogon}`;
  }
  if (wynik === 'za_czesto') {
    const s = typeof c.odczekaj_sekundy === 'number' ? c.odczekaj_sekundy : 60;
    return `Przed chwilą wysyłano ewidencję — odczekaj ${s} s.`;
  }
  if (wynik === 'brak_polaczenia') return 'Połączenie nie istnieje albo jest wyłączone.';
  if (wynik === 'blad') {
    const k = typeof c.komunikat === 'string' ? c.komunikat : '';
    if (k.includes('6001')) {
      return 'Użytkownik API nie ma uprawnienia „Synchronizacja Modułu kadr" — biuro włącza je w SaldeoSMART: Konfiguracja → Użytkownicy → edycja użytkownika → uprawnienia API.';
    }
    return k ? `Wysyłka nie powiodła się: ${k}` : 'Wysyłka ewidencji nie powiodła się.';
  }
  if (wynik === 'pominieto') {
    const powod = typeof c.powod === 'string' ? c.powod : '';
    const zdania: Record<string, string> = {
      zakres_wylaczony:
        'Zakres „Ewidencja czasu pracy i grafik" jest wyłączony — zaznacz go i zapisz połączenie.',
      wymaga_kadr:
        'Ewidencja wymaga włączonej „Kartoteki pracowników" — bez niej dokument nie ma do kogo trafić.',
      brak_sekretu: 'Brak tokenu w sejfie — uzupełnij połączenie.',
      brak_dokumentow_w_wtyczce: 'Ta wtyczka nie przyjmuje dokumentów kadrowych.',
      druk_nieskonfigurowany:
        'Organ druku nie jest skonfigurowany, więc nie ma z czego zrobić PDF-ów — zgłoś to administratorowi platformy.',
      miesiac_zamkniety:
        'Ten miesiąc został już wysłany. Kolejna wysyłka będzie korektą — użyj przycisku korekty.',
      automat_zablokowany:
        'Poprzednia wysyłka została odrzucona przez Saldeo — automat czeka na Twoją decyzję.',
      brak_zalogi: 'Żaden pracownik nie ma jeszcze akronimu — najpierw zsynchronizuj kartotekę.',
      brak_manifestu: 'Wtyczka nie jest znana synchronizatorowi kadr.',
      nieznany_konektor: 'Wtyczka nie jest znana synchronizatorowi kadr.',
    };
    return zdania[powod] ?? `Pominięto (${powod || 'bez powodu'}).`;
  }
  return 'Nieznany wynik wysyłki ewidencji.';
}

// -----------------------------------------------------------------------------
// „Diagnoza numerów" — wtyczki programu lojalnościowego (GoCRM)
// -----------------------------------------------------------------------------

/** Trasa nexus-core dla „Diagnozuj numery". */
export const TRASA_DIAGNOZY_TELEFONOW = '/api/nexus/genesis/connectors/gocrm/diagnoza-telefonow';

/** Ile numerów naraz — lustro `MAX_NUMEROW` z `gocrm/DiagnozaTelefonow.ts`. */
export const MAX_NUMEROW_DIAGNOZY = 15;

export function maDiagnozeTelefonow(manifest: ManifestWtyczki | undefined): boolean {
  return manifest?.diagnozaTelefonow === true;
}

/**
 * Pole tekstowe → lista numerów: jeden w wierszu, albo po przecinku czy
 * średniku. Walidację kształtu robi serwer — tu tylko rozcięcie i odrzucenie
 * pustych, żeby „Enter na końcu" nie był numerem.
 */
export function numeryZPola(tekst: string): string[] {
  return tekst
    .split(/[\n,;]+/)
    .map((n) => n.trim())
    .filter((n) => n.length > 0);
}

/** Kształt zapisu numeru tak, jak go oddaje diagnoza (bez pełnej wartości). */
export interface KsztaltNumeruPowloki {
  format: string;
  koncowka: string | null;
}

export interface KontaktDiagnozyPowloki {
  contact_id: number;
  telefon: KsztaltNumeruPowloki;
  karty: Array<KsztaltNumeruPowloki & { id: number | null; zgodna_z_telefonem: boolean }>;
  punkty: number | null;
  transakcje: number | null;
  ostatnia_transakcja: string | null;
  zgoda_marketingowa: boolean | null;
  znaleziony_przez: string[];
}

export interface WynikNumeruPowloki {
  numer: string;
  warianty: Array<{ wariant: string; status: number; liczba: number; uciete: boolean }>;
  kontakty: KontaktDiagnozyPowloki[];
  sygnaly: string[];
}

/** Zapis numeru dla człowieka: „48…1721", „+48…1721", „…1721 (krajowy)". */
export function opiszKsztaltNumeru(k: KsztaltNumeruPowloki): string {
  const ogon = k.koncowka ? `…${k.koncowka}` : '';
  switch (k.format) {
    case '+48':
      return `+48${ogon}`;
    case '48':
      return `48${ogon}`;
    case 'krajowy':
      return `${ogon} (krajowy)`;
    case 'brak':
      return 'brak';
    default:
      return `${ogon} (inny zapis)`;
  }
}

/**
 * Sygnał diagnozy → zdanie. Mapa jawna, jak przy synchronizatorach: sygnał,
 * którego powłoka nie zna, pokazuje się pod własnym kodem zamiast znikać.
 */
export const ZDANIA_SYGNALOW: Readonly<Record<string, string>> = {
  brak_trafien: 'Brak kontaktu z tym numerem w żadnym zapisie.',
  niepelny_wynik: 'Część zapytań nie przeszła albo lista jest ucięta — wynik niepełny.',
  duplikat: 'Kilka kontaktów dla jednego numeru.',
  karta_na_innym_rekordzie:
    'Karta jest tylko na części tych kontaktów — logowanie może trafiać na rekord bez karty.',
  rozne_formaty_zapisu: 'Numer zapisany w różnych kształtach (48…, +48…, krajowy).',
  rozne_zgody_marketingowe:
    'Kontakty różnią się zgodą marketingową — rezygnacja z SMS mogła trafić na inny rekord.',
};

export function zdanieSygnalu(sygnal: string): string {
  return ZDANIA_SYGNALOW[sygnal] ?? sygnal;
}

/** Odpowiedź `diagnoza-telefonow` → wynik albo jedno zdanie odmowy. */
export function opiszWynikDiagnozy(
  cialo: unknown
): { numery: WynikNumeruPowloki[] } | { blad: string } {
  const c = (cialo ?? {}) as Record<string, unknown>;
  switch (c.wynik) {
    case 'ok':
      return { numery: Array.isArray(c.numery) ? (c.numery as WynikNumeruPowloki[]) : [] };
    case 'zle_dane':
      return { blad: typeof c.komunikat === 'string' ? c.komunikat : 'Popraw listę numerów.' };
    case 'brak_polaczenia':
      return { blad: 'Wtyczka nie jest połączona — zapisz poświadczenia i spróbuj ponownie.' };
    case 'pominieto':
      return {
        blad:
          c.powod === 'brak_sekretu'
            ? 'Brak zapisanego Client secret — wpisz go w formularzu połączenia.'
            : 'Ta wtyczka nie obsługuje diagnozy numerów.',
      };
    case 'za_czesto':
      return {
        blad: `Diagnoza była przed chwilą — spróbuj za ${typeof c.odczekaj_sekundy === 'number' ? c.odczekaj_sekundy : 30} s.`,
      };
    case 'blad':
      return {
        blad: `GoCRM odmówił: ${typeof c.komunikat === 'string' ? c.komunikat : 'nieznany błąd'}.`,
      };
    default:
      return { blad: 'Nieznana odpowiedź serwera — nie wiem, czy diagnoza się wykonała.' };
  }
}
